import { useContext, useEffect, useMemo, useRef } from "react";
import {
	getSearchPageSyncTargetPath,
	getPathSegmentCount
} from "utils/search/searchPageUrlSync";
import { StatusContext } from "utils/routes/Status";

// Matches the "<lat>, <lng>" format pushSearchFromMapMoveByCoordinates (search.js)
// writes into the location query param for a map-drag search - see this file's
// header comment: "when the user drags the map we push the current pathname
// unchanged and only update the query (location=lat,lng)".
const COORDINATE_LOCATION_PATTERN = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/;

function isCoordinateLocationQuery(search) {
	const location = new URLSearchParams(search || "").get("location");
	return !!location && COORDINATE_LOCATION_PATTERN.test(location.trim());
}

/**
 * Detects a misspelled CLP city slug (e.g. /self-storage/pennsylvania/mechancsbrg/)
 * from a state-only cityUrl/canonical fallback, and:
 * - returns `redirectPath`/`canonical` for the caller to override the canonical tag
 *   with the corrected URL
 * - sets the SSR 301 directly on StatusContext (see below for why not <Status>)
 * - runs a client-side navigate(..., { replace: true }) so SPA navigation
 *   (internal link, back/forward) gets the same correction, since StatusContext
 *   has no provider on a client-side-only route change
 *
 * Shared by SearchPageV1.js and SearchPageV2.js so the misspelling-recovery
 * and query-string-preservation logic only needs to be correct in one place.
 *
 * @param {Object} opts
 * @param {string} opts.pathname - Current location pathname
 * @param {string} opts.cityUrl - City path from API (e.g. /self-storage/california/mountain-view/)
 * @param {string} [opts.canonical] - Raw, un-overridden canonical from the API response
 * @param {string} [opts.city] - The gateway's resolved location.city for this request
 * @param {string} [opts.fullState] - The gateway's resolved location.fullState; must match the
 *   pathname's state for recovery to run
 * @param {string} opts.baseUrl - Site base URL, used to build the absolute canonical override
 * @param {string} opts.search - Current location.search (query string), preserved on redirect/navigate
 * @param {Function} opts.navigate - react-router navigate function
 * @returns {{redirectPath: string|null, canonical: string|null}}
 */
export default function useMisspelledCityRedirect({
	pathname,
	cityUrl,
	canonical,
	city,
	fullState,
	baseUrl,
	search,
	navigate
}) {
	// Tracks which pathname the current cityUrl/canonical/city snapshot is
	// actually fresh for. A pathname-only client-side navigation never
	// refetches (see getSearchPageSyncTargetPath's isPathnameStale doc), so
	// comparing pathname against this ref - updated only when the underlying
	// data itself changes, not merely when pathname does - detects that
	// staleness without needing any cross-request state. Mutating a ref
	// during render (rather than in an effect) is intentional: the flag must
	// be correct for THIS render's recovery decision below, not the next one.
	const dataKeyRef = useRef(null);
	const freshPathnameRef = useRef(pathname);
	const dataKey = `${cityUrl || ""}|${canonical || ""}|${city || ""}|${fullState || ""}`;
	if (dataKeyRef.current !== dataKey) {
		dataKeyRef.current = dataKey;
		freshPathnameRef.current = pathname;
	}
	const isPathnameStale = freshPathnameRef.current !== pathname;
	const isCoordinateSearch = isCoordinateLocationQuery(search);

	const redirectPath = useMemo(() => {
		if (getPathSegmentCount(pathname) !== 3) return null;
		const targetPath = getSearchPageSyncTargetPath({
			pathname,
			cityUrl,
			canonical,
			city,
			fullState,
			isPathnameStale,
			isCoordinateSearch
		});
		return targetPath && getPathSegmentCount(targetPath) === 3
			? targetPath
			: null;
	}, [
		pathname,
		cityUrl,
		canonical,
		city,
		fullState,
		isPathnameStale,
		isCoordinateSearch
	]);

	const redirectURL = redirectPath ? `${redirectPath}${search || ""}` : null;

	// Writes ctx.status/redirectURL directly (the same fields <Status> would
	// set) instead of the caller wrapping its returned tree in <Status> when
	// redirectPath is truthy. That conditional wrap changes the root element
	// type between renders (<Status> vs the page component itself), and React
	// remounts the whole subtree on a type change - so the moment the sync
	// effect below corrects the URL and redirectPath goes back to null, the
	// map instance, open filter panel, and scroll position would all reset.
	// Setting ctx here keeps the caller's returned tree shape constant.
	const statusCtx = useContext(StatusContext);
	if (statusCtx && redirectURL) {
		statusCtx.status = 301;
		statusCtx.redirectURL = redirectURL;
	}
	useEffect(() => {
		if (statusCtx && redirectURL) {
			statusCtx.status = 301;
			statusCtx.redirectURL = redirectURL;
		}
	}, [statusCtx, redirectURL]);

	// Sync URL path to API canonical/city when they differ; preserve amenity
	// segments (pre-existing behavior beyond just misspelling recovery).
	useEffect(() => {
		const targetPath = getSearchPageSyncTargetPath({
			pathname,
			cityUrl,
			canonical,
			city,
			fullState,
			isPathnameStale,
			isCoordinateSearch
		});
		if (!targetPath) return;
		const hash =
			typeof globalThis.window === "undefined"
				? "#searchPage"
				: globalThis.location.hash || "#searchPage";
		navigate(`${targetPath}${search || ""}${hash}`, { replace: true });
	}, [
		cityUrl,
		pathname,
		search,
		navigate,
		canonical,
		city,
		fullState,
		isPathnameStale,
		isCoordinateSearch
	]);

	return {
		redirectPath,
		canonical: redirectPath ? `${baseUrl}${redirectPath}` : null
	};
}
